import { lazy, Suspense, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { MessageDto, OutboundMediaCapabilitiesResponse } from "@multi-chat/shared";
import {
  SendHorizontal as SendIcon,
  Ban as BanIcon,
  CircleX as CloseIcon,
  Clock as ClockIcon,
  FileText as FileTextIcon,
  MessageSquareOff as MessageSquareOffIcon,
  Mic as MicIcon,
  Paperclip as PaperclipIcon,
  Smile as EmojiIcon,
} from "lucide-react";
import { isApiError } from "../../../api/client.js";
import { cancelConversationAttachment, uploadConversationAttachment } from "../../../api/outbound-media-api.js";
import { Button } from "../../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover/index.js";
import { Sheet, SheetContent, SheetTitle } from "../../../components/ui/sheet/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { useMinWidth } from "../../../components/layout/use-min-width.js";
import { cn } from "../../../lib/utils.js";
import type { ComposerMode } from "../composer-capability.js";
import { isPlatformNotEnabledCode } from "../composer-platform-entitlement.js";
import { insertAtCaret } from "../composer-insert.js";
import { shouldSubmitComposerOnKeyDown } from "../composer-keyboard.js";
import { useConversationDraftStore } from "../conversation-draft-context.js";
import { applyConversationDraftSendResult } from "../conversation-draft-store.js";
import { mapOutboundMediaUploadError, mapOutboundMediaValidationError } from "../outbound-media-errors.js";
import {
  buildOutboundMediaAcceptAttribute,
  validateOutboundMediaFile,
} from "../outbound-media-validation.js";
import {
  recordedVoiceFilename,
  resolveOutboundVoiceRecorderCapability,
} from "../outbound-voice-recorder-capability.js";
import { useComposerVoiceRecorder } from "../use-composer-voice-recorder.js";
import { ReplyQuoteStrip } from "../MessageReplyPreview/ReplyQuoteStrip.js";
import { replyQuoteFromMessage } from "../MessageReplyPreview/reply-quote-model.js";
import { ComposerAttachmentPreview } from "./ComposerAttachmentPreview.js";
import { ComposerVoicePreview } from "./ComposerVoicePreview.js";

const COMPOSER_MIN_HEIGHT_PX = 40;
const COMPOSER_MAX_HEIGHT_PX = 168;
const COMPOSER_TOOL_CLASS =
  "size-[30px] shrink-0 rounded-lg p-0 text-inbox-muted hover:bg-inbox-hover hover:text-inbox-strong";
const COMPOSER_MORPH_ITEM_CLASS =
  "col-start-1 row-start-1 justify-self-end transition-[opacity,transform] duration-150 ease-inbox motion-reduce:transition-none";
const EMOJI_POPOVER_MIN_WIDTH_PX = 768;

const ComposerEmojiPicker = lazy(async () => {
  const mod = await import("./ComposerEmojiPicker.js");
  return { default: mod.ComposerEmojiPicker };
});

const fieldErrorClass = "m-0 text-[0.8rem] text-destructive";

type ComposerAttachmentState =
  | { kind: "idle" }
  | {
      kind: "uploading";
      file: File;
      previewUrl: string;
      attachmentType: string;
      recordedVoice?: boolean;
      recordedVoiceDurationMs?: number;
    }
  | {
      kind: "ready";
      attachmentId: string;
      attachmentType: string;
      fileName: string | null;
      fileSizeBytes: number;
      previewUrl: string | null;
      recordedVoice?: boolean;
      recordedVoiceDurationMs?: number;
    };

export function ConversationComposer({
  conversationId,
  composerId,
  mode,
  capabilityPending = false,
  platformDisabled = false,
  showTemplate,
  sending,
  sendError,
  replyTarget,
  contactLabel,
  organizationId,
  mediaCapabilities,
  onClearReply,
  onSend,
  onOpenTemplate,
  onClearSendError,
  onPlatformNotEnabled,
  templateTriggerRef,
  workflowClosed = false,
  recordingStopped = false,
  reopenPending = false,
  onReopen,
  onRecordingDiscarded,
}: {
  conversationId: number;
  composerId: string;
  mode: ComposerMode;
  /** Details for this conversation are not loaded yet. Not a closed or disabled channel. */
  capabilityPending?: boolean;
  platformDisabled?: boolean;
  showTemplate: boolean;
  sending: boolean;
  sendError: string | null;
  replyTarget?: MessageDto | null;
  contactLabel: string;
  organizationId: number;
  mediaCapabilities: OutboundMediaCapabilitiesResponse | null;
  onClearReply?: () => void;
  onSend: (
    content: string,
    options?: { attachmentId?: string },
  ) => Promise<boolean>;
  onOpenTemplate: () => void;
  onClearSendError: () => void;
  /** Upload rejected with PLATFORM_NOT_ENABLED; the parent switches to the blocked state. */
  onPlatformNotEnabled?: () => void;
  templateTriggerRef?: RefObject<HTMLButtonElement | null>;
  /** Keeps the composer mounted while a closed notice covers it. */
  workflowClosed?: boolean;
  recordingStopped?: boolean;
  reopenPending?: boolean;
  onReopen?: () => void;
  onRecordingDiscarded?: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const draftStore = useConversationDraftStore();
  const useEmojiPopover = useMinWidth(EMOJI_POPOVER_MIN_WIDTH_PX);
  const emojiSheetTitleId = useId();
  const [draft, setDraftState] = useState(
    () => draftStore.getDraft(conversationId) ?? "",
  );
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachmentState, setAttachmentState] = useState<ComposerAttachmentState>({
    kind: "idle",
  });
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const selectionRef = useRef({ start: 0, end: 0 });
  const selectionTouchedRef = useRef(false);
  const pendingCaretRef = useRef<number | null>(null);
  const trimmedDraft = draft.trim();
  const isFreeform =
    mode === "whatsapp_freeform" ||
    mode === "facebook_freeform" ||
    mode === "instagram_freeform";
  const showFreeformChrome = isFreeform && !platformDisabled && !capabilityPending;
  const uploading = attachmentState.kind === "uploading";
  const readyAttachment = attachmentState.kind === "ready" ? attachmentState : null;
  const voiceRecorderCapability = resolveOutboundVoiceRecorderCapability(mediaCapabilities);
  const voiceRecorder = useComposerVoiceRecorder({
    capability: voiceRecorderCapability,
    disabled:
      workflowClosed ||
      platformDisabled ||
      capabilityPending ||
      !isFreeform ||
      sending ||
      uploading ||
      attachmentState.kind !== "idle",
  });
  const voiceRecordingActive = voiceRecorder.phase === "recording";
  const voicePreviewActive = voiceRecorder.phase === "preview" && voiceRecorder.recording != null;
  const composerBusy = sending || uploading || voiceRecordingActive;
  const canAttach =
    isFreeform &&
    !platformDisabled &&
    !capabilityPending &&
    mediaCapabilities != null &&
    attachmentState.kind === "idle" &&
    voiceRecorder.phase === "idle";
  const canSubmit =
    isFreeform &&
    !platformDisabled &&
    !capabilityPending &&
    !composerBusy &&
    (trimmedDraft.length > 0 || readyAttachment != null);
  const uiDir = i18n.language === "he" ? "rtl" : "ltr";
  const textareaDir = draft.length === 0 ? uiDir : "auto";
  const mediaAccept =
    mediaCapabilities != null ? buildOutboundMediaAcceptAttribute(mediaCapabilities) : undefined;
  const canRecordVoice =
    isFreeform &&
    !platformDisabled &&
    !capabilityPending &&
    voiceRecorderCapability.supported &&
    attachmentState.kind === "idle" &&
    voiceRecorder.phase === "idle";

  const replyFocusId = replyTarget?.messageId ?? null;
  useLayoutEffect(() => {
    if (replyFocusId == null || !isFreeform || workflowClosed) return;
    const frame = window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [replyFocusId, isFreeform, workflowClosed]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, COMPOSER_MIN_HEIGHT_PX), COMPOSER_MAX_HEIGHT_PX)}px`;
    const caret = pendingCaretRef.current;
    if (caret == null) return;
    if (
      !isFreeform ||
      composerBusy
    ) {
      return;
    }
    pendingCaretRef.current = null;
    el.focus();
    el.setSelectionRange(caret, caret);
  }, [draft, isFreeform, composerBusy, textareaDir]);

  useEffect(() => {
    return () => {
      uploadAbortRef.current?.abort();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [conversationId]);

  useEffect(() => {
    uploadAbortRef.current?.abort();
    voiceRecorder.resetRecorder();
    revokePreviewUrl();
    setAttachmentState({ kind: "idle" });
    setAttachmentError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [conversationId, voiceRecorder.resetRecorder]);

  useEffect(() => {
    if (!workflowClosed) return;
    if (voiceRecorder.phase !== "recording") return;
    voiceRecorder.discardRecording();
    onRecordingDiscarded?.();
  }, [onRecordingDiscarded, voiceRecorder.discardRecording, voiceRecorder.phase, workflowClosed]);

  useEffect(() => {
    if (workflowClosed || !voicePreviewActive || !voiceRecorder.recording || attachmentState.kind !== "idle") {
      return;
    }
    void uploadRecordedVoice(voiceRecorder.recording);
  }, [attachmentState.kind, voicePreviewActive, voiceRecorder.recording]);

  function revokePreviewUrl(): void {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }

  function reportUploadError(error: unknown): void {
    if (isApiError(error) && isPlatformNotEnabledCode(error.code)) {
      setAttachmentError(null);
      onPlatformNotEnabled?.();
      return;
    }
    setAttachmentError(mapOutboundMediaUploadError(t, error));
  }

  function resetAttachmentAfterSend(): void {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    voiceRecorder.discardRecording();
    revokePreviewUrl();
    setAttachmentState({ kind: "idle" });
    setAttachmentError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function clearAttachment(): void {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    voiceRecorder.discardRecording();
    const stagedId =
      attachmentState.kind === "ready" ? attachmentState.attachmentId : null;
    revokePreviewUrl();
    setAttachmentState({ kind: "idle" });
    setAttachmentError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (stagedId) {
      void cancelConversationAttachment(organizationId, conversationId, stagedId).catch(() => {
        // Best-effort server cleanup; orphan TTL worker covers abandoned staging.
      });
    }
  }

  async function uploadRecordedVoice(recording: NonNullable<typeof voiceRecorder.recording>): Promise<void> {
    if (platformDisabled) {
      setAttachmentError(t("inbox.composer.platformDisabled"));
      return;
    }
    onClearSendError();
    setAttachmentError(null);
    const file = new File([recording.blob], recordedVoiceFilename(recording.mimeType), {
      type: recording.mimeType,
    });

    revokePreviewUrl();
    previewUrlRef.current = recording.previewUrl;

    uploadAbortRef.current?.abort();
    const controller = new AbortController();
    uploadAbortRef.current = controller;

    setAttachmentState({
      kind: "uploading",
      file,
      previewUrl: recording.previewUrl,
      attachmentType: "AUDIO",
      recordedVoice: true,
      recordedVoiceDurationMs: recording.durationMs,
    });

    try {
      const uploaded = await uploadConversationAttachment(
        organizationId,
        conversationId,
        file,
        controller.signal,
        { recordedVoice: true, recordedVoiceDurationMs: recording.durationMs },
      );
      if (controller.signal.aborted) {
        return;
      }
      setAttachmentState({
        kind: "ready",
        attachmentId: uploaded.attachmentId,
        attachmentType: uploaded.attachmentType,
        fileName: uploaded.fileName,
        fileSizeBytes: Number(uploaded.fileSizeBytes),
        previewUrl: recording.previewUrl,
        recordedVoice: true,
        recordedVoiceDurationMs: recording.durationMs,
      });
      setAttachmentError(null);
      voiceRecorder.discardRecording();
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      revokePreviewUrl();
      setAttachmentState({ kind: "idle" });
      voiceRecorder.discardRecording();
      reportUploadError(error);
    } finally {
      if (uploadAbortRef.current === controller) {
        uploadAbortRef.current = null;
      }
    }
  }

  function queueComposerFocus(caret: number): void {
    pendingCaretRef.current = caret;
  }

  function setDraft(next: string): void {
    setDraftState(next);
    draftStore.setDraft(conversationId, next);
  }

  function rememberSelection(): void {
    const el = textareaRef.current;
    if (!el) return;
    selectionTouchedRef.current = true;
    selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
  }

  function handleEmojiSelect(emoji: string): void {
    onClearSendError();
    const start = selectionTouchedRef.current ? selectionRef.current.start : draft.length;
    const end = selectionTouchedRef.current ? selectionRef.current.end : draft.length;
    const next = insertAtCaret(draft, emoji, start, end);
    selectionTouchedRef.current = true;
    selectionRef.current = { start: next.caret, end: next.caret };
    pendingCaretRef.current = next.caret;
    setDraft(next.value);
    if (!useEmojiPopover) {
      setEmojiOpen(false);
    }
  }

  async function submitDraft(): Promise<void> {
    if (!canSubmit) return;
    const attachmentId = readyAttachment?.attachmentId;
    const ok = await onSend(trimmedDraft, attachmentId ? { attachmentId } : undefined);
    applyConversationDraftSendResult(draftStore, conversationId, ok);
    if (ok) {
      queueComposerFocus(0);
      setDraftState("");
      selectionTouchedRef.current = false;
      selectionRef.current = { start: 0, end: 0 };
      setEmojiOpen(false);
      resetAttachmentAfterSend();
      return;
    }
    queueComposerFocus(
      selectionTouchedRef.current ? selectionRef.current.start : draft.length,
    );
  }

  async function handleAttachmentSelected(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (capabilityPending) {
      event.target.value = "";
      return;
    }
    if (platformDisabled) {
      event.target.value = "";
      setAttachmentError(t("inbox.composer.platformDisabled"));
      return;
    }
    onClearSendError();
    setAttachmentError(null);
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !mediaCapabilities) {
      return;
    }

    const validation = validateOutboundMediaFile({ file, capabilities: mediaCapabilities });
    if (!validation.ok) {
      setAttachmentError(mapOutboundMediaValidationError(t, validation.code));
      return;
    }

    revokePreviewUrl();
    const previewUrl =
      validation.attachmentType === "IMAGE" ||
      validation.attachmentType === "VIDEO" ||
      validation.attachmentType === "STICKER"
        ? URL.createObjectURL(file)
        : null;
    previewUrlRef.current = previewUrl;

    uploadAbortRef.current?.abort();
    const controller = new AbortController();
    uploadAbortRef.current = controller;

    setAttachmentState({
      kind: "uploading",
      file,
      previewUrl: previewUrl ?? "",
      attachmentType: validation.attachmentType,
    });

    try {
      const uploaded = await uploadConversationAttachment(
        organizationId,
        conversationId,
        file,
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      setAttachmentState({
        kind: "ready",
        attachmentId: uploaded.attachmentId,
        attachmentType: uploaded.attachmentType,
        fileName: uploaded.fileName,
        fileSizeBytes: Number(uploaded.fileSizeBytes),
        previewUrl,
      });
      setAttachmentError(null);
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      revokePreviewUrl();
      setAttachmentState({ kind: "idle" });
      reportUploadError(error);
    } finally {
      if (uploadAbortRef.current === controller) {
        uploadAbortRef.current = null;
      }
    }
  }

  async function handleSend(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    await submitDraft();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
    rememberSelection();
    if (
      !shouldSubmitComposerOnKeyDown({
        key: event.key,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        isComposing: event.nativeEvent.isComposing,
        keyCode: event.nativeEvent.keyCode,
        canSubmit,
      })
    ) {
      return;
    }
    event.preventDefault();
    void submitDraft();
  }

  const sendLabel = sending
    ? t("inbox.composer.sending")
    : uploading
      ? t("inbox.composer.attachmentUploading")
      : t("inbox.composer.send");
  const attachLabel = t("inbox.composer.attach");
  const voiceRecorderLabel = t("inbox.composer.voiceRecorder.start");
  const attachmentRemoveLabel = t("inbox.composer.attachmentRemove");
  const emojiLabel = t("inbox.composer.emoji");
  const pickerFallback = (
    <div className="grid h-[20rem] place-items-center text-[0.8rem] text-ink-subtle">
      {t("inbox.composer.emojiLoading")}
    </div>
  );

  const emojiTriggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={COMPOSER_TOOL_CLASS}
      aria-label={emojiLabel}
      aria-expanded={emojiOpen}
      aria-haspopup="dialog"
      disabled={composerBusy}
      onMouseDown={(event) => {
        event.preventDefault();
        rememberSelection();
      }}
    >
      <EmojiIcon size={17} aria-hidden="true" />
    </Button>
  );

  const attachmentPreview =
    voiceRecordingActive || (voicePreviewActive && attachmentState.kind === "idle") ? (
      <ComposerVoicePreview
        previewUrl={voiceRecorder.recording?.previewUrl ?? null}
        durationMs={
          voiceRecordingActive ? voiceRecorder.elapsedMs : (voiceRecorder.recording?.durationMs ?? 0)
        }
        recording={voiceRecordingActive}
        liveWaveformBarHeights={voiceRecorder.liveWaveformBarHeights}
        liveWaveformProgress={voiceRecorder.liveWaveformProgress}
        removeLabel={attachmentRemoveLabel}
        onRemove={() => {
          voiceRecorder.discardRecording();
          setAttachmentError(null);
        }}
        onStopRecording={() => voiceRecorder.stopRecording()}
      />
    ) : attachmentState.kind === "uploading" && attachmentState.recordedVoice ? (
      <ComposerVoicePreview
        previewUrl={attachmentState.previewUrl || null}
        durationMs={attachmentState.recordedVoiceDurationMs ?? 0}
        recording={false}
        removeLabel={attachmentRemoveLabel}
        onRemove={clearAttachment}
        onStopRecording={() => undefined}
      />
    ) : attachmentState.kind === "ready" && attachmentState.recordedVoice ? (
      <ComposerVoicePreview
        previewUrl={attachmentState.previewUrl}
        durationMs={attachmentState.recordedVoiceDurationMs ?? 0}
        recording={false}
        removeLabel={attachmentRemoveLabel}
        onRemove={clearAttachment}
        onStopRecording={() => undefined}
      />
    ) : attachmentState.kind === "uploading" ? (
      <ComposerAttachmentPreview
        attachmentType={attachmentState.attachmentType}
        fileName={attachmentState.file.name}
        fileSizeBytes={attachmentState.file.size}
        previewUrl={attachmentState.previewUrl || null}
        uploading
        removeLabel={attachmentRemoveLabel}
        onRemove={clearAttachment}
      />
    ) : attachmentState.kind === "ready" ? (
      <ComposerAttachmentPreview
        attachmentType={attachmentState.attachmentType}
        fileName={attachmentState.fileName}
        fileSizeBytes={attachmentState.fileSizeBytes}
        previewUrl={attachmentState.previewUrl}
        uploading={false}
        removeLabel={attachmentRemoveLabel}
        onRemove={clearAttachment}
      />
    ) : null;

  const emojiPicker = emojiOpen ? (
    <Suspense fallback={pickerFallback}>
      <ComposerEmojiPicker onSelect={handleEmojiSelect} />
    </Suspense>
  ) : null;

  const windowClosedCard =
    !platformDisabled && !capabilityPending && isComposerWindowClosedMode(mode);
  const showMicMorph =
    voiceRecorderCapability.supported &&
    trimmedDraft.length === 0 &&
    attachmentState.kind === "idle" &&
    voiceRecorder.phase === "idle";

  return (
    <footer
      className={cn(
        "relative z-[2] flex shrink-0 flex-col gap-1.5 bg-inbox-thread px-4 pt-0.5 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] max-md:px-2 max-md:pb-[max(0.625rem,env(safe-area-inset-bottom,0px))]",
        "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-[18px] before:bg-gradient-to-t before:from-inbox-thread before:to-transparent",
      )}
      aria-label={t("inbox.composer.label")}
    >
      <form
        className={cn(
          "group/composer relative flex flex-col rounded-[14px] border transition-[border-color,box-shadow] duration-200",
          workflowClosed || windowClosedCard
            ? "border-inbox-calm-line bg-inbox-calm-surface shadow-[0_1px_2px_rgb(60_45_10/0.04),0_8px_22px_-14px_rgb(60_45_10/0.18)]"
            : "border-[#e0e8e5] bg-surface shadow-inbox-composer",
          showFreeformChrome &&
            "focus-within:border-[rgb(23_126_103/0.42)] focus-within:shadow-inbox-composer-focus",
        )}
        onSubmit={(event) => void handleSend(event)}
      >
        {workflowClosed ? (
          <ComposerNoticeRow
            testId="inbox-closed-notice"
            calm
            icon={<CloseIcon size={15} strokeWidth={2} aria-hidden="true" />}
            title={t("inbox.workflow.status.closedTitle")}
            body={
              recordingStopped
                ? t("inbox.workflow.status.recordingStopped")
                : t("inbox.workflow.status.closedBody")
            }
            actionLabel={t("inbox.workflow.status.reopen")}
            actionDisabled={reopenPending}
            onAction={onReopen}
          />
        ) : null}
        <div className={workflowClosed ? "sr-only" : undefined} inert={workflowClosed || undefined}>
        {platformDisabled ? (
          <ComposerUnavailableState
            mode={mode}
            platformDisabled
            showTemplate={false}
            sending={sending}
            onOpenTemplate={onOpenTemplate}
          />
        ) : capabilityPending ? (
          <div
            className="flex min-h-[52px] items-center px-3.5"
            role="status"
            aria-busy="true"
            aria-label={t("inbox.loadingMessages")}
            data-testid="composer-capability-pending"
          >
            <span className="h-4 w-2/3 animate-pulse rounded-md bg-inbox-field" />
          </div>
        ) : showFreeformChrome ? (
          <>
            {replyTarget || attachmentPreview ? (
              <div className="flex flex-col gap-1.5 px-2 pt-2">
                {replyTarget ? (
                  <div data-testid="composer-reply-preview">
                    <ReplyQuoteStrip
                      presentation={replyQuoteFromMessage(replyTarget, contactLabel, t)}
                      organizationId={organizationId}
                      dismissLabel={onClearReply ? t("inbox.replyTo.clear") : undefined}
                      onDismiss={onClearReply}
                      className="rounded-[10px] bg-inbox-field"
                    />
                  </div>
                ) : null}
                {attachmentPreview}
              </div>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              accept={mediaAccept}
              tabIndex={-1}
              aria-hidden="true"
              disabled={!canAttach || composerBusy}
              onChange={(event) => void handleAttachmentSelected(event)}
            />
            <label className="sr-only" htmlFor={composerId}>
              {t("inbox.composer.placeholder")}
            </label>
            <textarea
              id={composerId}
              ref={textareaRef}
              className={cn(
                "block min-h-10 max-h-[168px] w-full resize-none overflow-y-auto border-0 bg-transparent px-3.5 pt-2.5 pb-0.5 text-start text-[0.84375rem] font-normal leading-[1.55] text-inbox-strong outline-none placeholder:text-[#9ca9a5] focus-visible:shadow-none read-only:opacity-65",
                composerBusy && "opacity-65",
              )}
              dir={textareaDir}
              value={draft}
              onChange={(event) => {
                onClearSendError();
                setAttachmentError(null);
                setDraft(event.target.value);
                selectionTouchedRef.current = true;
                selectionRef.current = {
                  start: event.target.selectionStart,
                  end: event.target.selectionEnd,
                };
              }}
              onSelect={rememberSelection}
              onKeyUp={rememberSelection}
              onClick={rememberSelection}
              onKeyDown={handleKeyDown}
              placeholder={t("inbox.composer.placeholder")}
              rows={1}
              readOnly={composerBusy}
              aria-busy={composerBusy}
              maxLength={4096}
              enterKeyHint="send"
            />
          </>
        ) : (
          <ComposerUnavailableState
            mode={mode}
            showTemplate={showTemplate}
            sending={sending}
            templateTriggerRef={templateTriggerRef}
            onOpenTemplate={() => {
              onClearSendError();
              onOpenTemplate();
            }}
          />
        )}

        {showFreeformChrome ? (
          <div className="@container/composer-tools flex min-w-0 items-center gap-px px-[7px] pb-[7px]">
            {useEmojiPopover ? (
              <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                <SimpleTooltip content={emojiLabel}>
                  <PopoverTrigger asChild>{emojiTriggerButton}</PopoverTrigger>
                </SimpleTooltip>
                <PopoverContent
                  align="start"
                  side="top"
                  sideOffset={8}
                  collisionPadding={12}
                  className="w-[min(18.75rem,calc(100vw-1.5rem))] overflow-hidden p-0"
                  onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    textareaRef.current?.focus();
                  }}
                >
                  {emojiPicker}
                </PopoverContent>
              </Popover>
            ) : (
              <>
                <SimpleTooltip content={emojiLabel}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={COMPOSER_TOOL_CLASS}
                    aria-label={emojiLabel}
                    aria-expanded={emojiOpen}
                    aria-haspopup="dialog"
                    disabled={composerBusy}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      rememberSelection();
                    }}
                    onClick={() => setEmojiOpen(true)}
                  >
                    <EmojiIcon size={17} aria-hidden="true" />
                  </Button>
                </SimpleTooltip>
                <Sheet open={emojiOpen} onOpenChange={setEmojiOpen}>
                  <SheetContent
                    side="bottom"
                    aria-labelledby={emojiSheetTitleId}
                    className="max-h-[min(70dvh,24rem)] gap-0 overflow-hidden p-0 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]"
                    onCloseAutoFocus={(event) => {
                      event.preventDefault();
                      textareaRef.current?.focus();
                    }}
                  >
                    <SheetTitle id={emojiSheetTitleId} className="sr-only">
                      {emojiLabel}
                    </SheetTitle>
                    {emojiPicker}
                  </SheetContent>
                </Sheet>
              </>
            )}

            {mediaCapabilities ? (
              <SimpleTooltip content={attachLabel}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={COMPOSER_TOOL_CLASS}
                  aria-label={attachLabel}
                  disabled={!canAttach || composerBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <PaperclipIcon size={17} aria-hidden="true" />
                </Button>
              </SimpleTooltip>
            ) : null}

            {showTemplate ? (
              <SimpleTooltip content={t("inbox.composer.sendTemplate")}>
                <Button
                  ref={templateTriggerRef}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={COMPOSER_TOOL_CLASS}
                  aria-label={t("inbox.composer.sendTemplate")}
                  disabled={composerBusy}
                  onClick={() => {
                    onClearSendError();
                    onOpenTemplate();
                  }}
                >
                  <FileTextIcon size={17} aria-hidden="true" />
                </Button>
              </SimpleTooltip>
            ) : null}

            <div className="ms-auto flex min-w-0 items-center gap-2.5">
              <span
                aria-hidden="true"
                className={cn(
                  "min-w-0 truncate text-[0.6875rem] text-inbox-subtle opacity-0 transition-opacity duration-150 motion-reduce:transition-none max-md:hidden @max-[30rem]/composer-tools:hidden",
                  !showMicMorph && "group-focus-within/composer:opacity-100",
                )}
              >
                {t("inbox.composer.keyboardHint")}
              </span>
              <div className="grid h-8 min-w-8 shrink-0">
                {voiceRecorderCapability.supported ? (
                  <SimpleTooltip content={voiceRecorderLabel}>
                    <button
                      type="button"
                      className={cn(
                        COMPOSER_MORPH_ITEM_CLASS,
                        "grid size-8 place-items-center rounded-full text-inbox-muted hover:bg-inbox-hover hover:text-inbox-strong disabled:opacity-50",
                        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
                        showMicMorph ? "opacity-100" : "pointer-events-none scale-75 opacity-0",
                      )}
                      aria-label={voiceRecorderLabel}
                      inert={!showMicMorph || undefined}
                      disabled={!canRecordVoice || composerBusy}
                      onClick={() => void voiceRecorder.startRecording()}
                    >
                      <MicIcon size={17} aria-hidden="true" />
                    </button>
                  </SimpleTooltip>
                ) : null}
                <SimpleTooltip content={sendLabel}>
                  <button
                    type="submit"
                    className={cn(
                      COMPOSER_MORPH_ITEM_CLASS,
                      "inline-flex h-8 items-center gap-1.5 rounded-full bg-primary ps-[11px] pe-[13px] text-[0.78125rem] font-semibold text-white shadow-inbox-send hover:bg-primary-hover",
                      "max-md:w-9 max-md:justify-center max-md:px-0",
                      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                      "disabled:cursor-not-allowed disabled:shadow-none disabled:hover:bg-primary",
                      showMicMorph
                        ? "pointer-events-none scale-75 opacity-0"
                        : "opacity-100 disabled:opacity-[0.42]",
                    )}
                    inert={showMicMorph || undefined}
                    disabled={!canSubmit}
                    aria-label={sendLabel}
                    onMouseDown={(event) => {
                      if (event.button !== 0 || !canSubmit) return;
                      event.preventDefault();
                    }}
                  >
                    <SendIcon size={16} strokeWidth={2} className="rtl:-scale-x-100" aria-hidden="true" />
                    <span className="max-md:hidden">{t("inbox.composer.send")}</span>
                  </button>
                </SimpleTooltip>
              </div>
            </div>
          </div>
        ) : null}
        </div>
      </form>
      {attachmentError && !platformDisabled ? (
        <p className={fieldErrorClass} role="alert">
          {attachmentError}
        </p>
      ) : null}
      {voiceRecorder.permissionDenied && !platformDisabled ? (
        <p className={fieldErrorClass} role="alert">
          {t("inbox.composer.voiceRecorder.permissionDenied")}
        </p>
      ) : null}
      {sendError ? (
        <p className={fieldErrorClass} role="alert">
          {sendError}
        </p>
      ) : null}
    </footer>
  );
}

function ComposerUnavailableState({
  mode,
  platformDisabled = false,
  showTemplate,
  sending,
  templateTriggerRef,
  onOpenTemplate,
}: {
  mode: ComposerMode;
  platformDisabled?: boolean;
  showTemplate: boolean;
  sending: boolean;
  templateTriggerRef?: RefObject<HTMLButtonElement | null>;
  onOpenTemplate: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isWindowClosed = !platformDisabled && isComposerWindowClosedMode(mode);
  const copy = platformDisabled
    ? {
        title: t("inbox.composer.platformDisabledTitle"),
        body: t("inbox.composer.platformDisabledBody"),
      }
    : composerNoticeCopy(mode, t);

  return (
    <ComposerNoticeRow
      testId={platformDisabled ? "composer-platform-disabled" : "composer-unavailable"}
      calm={isWindowClosed}
      icon={
        platformDisabled ? (
          <BanIcon size={15} strokeWidth={2} aria-hidden="true" />
        ) : isWindowClosed ? (
          <ClockIcon size={15} strokeWidth={2} aria-hidden="true" />
        ) : (
          <MessageSquareOffIcon size={15} strokeWidth={2} aria-hidden="true" />
        )
      }
      title={copy.title}
      body={copy.body}
      actionLabel={isWindowClosed && showTemplate ? t("inbox.composer.chooseTemplate") : undefined}
      actionDisabled={sending}
      actionRef={templateTriggerRef}
      onAction={onOpenTemplate}
    />
  );
}

/** Shared banner row for the reply-window notice and the closed-conversation notice. */
function ComposerNoticeRow({
  testId,
  calm,
  icon,
  title,
  body,
  actionLabel,
  actionDisabled,
  actionRef,
  onAction,
}: {
  testId: string;
  calm: boolean;
  icon: ReactNode;
  title: string;
  body: string;
  actionLabel?: string;
  actionDisabled?: boolean;
  actionRef?: RefObject<HTMLButtonElement | null>;
  onAction?: () => void;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-[11px] py-2.5 ps-3 pe-2.5 max-md:flex-wrap"
      role="status"
      data-testid={testId}
    >
      <span
        className={cn(
          "mt-px inline-grid size-7 shrink-0 place-items-center self-start rounded-[9px]",
          calm ? "bg-inbox-calm-bg text-inbox-calm" : "bg-inbox-field text-inbox-muted",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "m-0 text-[0.8125rem] leading-[1.4] font-semibold",
            calm ? "text-[#2f2a1d]" : "text-inbox-strong",
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            "m-0 mt-px text-[0.75rem] leading-normal",
            calm ? "text-[#6f6a5c]" : "text-inbox-muted",
          )}
        >
          {body}
        </p>
      </div>
      {actionLabel && onAction ? (
        <Button
          ref={actionRef}
          type="button"
          size="sm"
          className="h-[30px] shrink-0 rounded-lg px-[11px] text-[0.78125rem] max-md:h-[38px] max-md:w-full"
          disabled={actionDisabled}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function isComposerWindowClosedMode(mode: ComposerMode): boolean {
  return (
    mode === "whatsapp_window_closed" ||
    mode === "facebook_window_closed" ||
    mode === "instagram_window_closed"
  );
}

/** Per-provider notice: WhatsApp can still send a template; Meta channels wait for the customer. */
function composerNoticeCopy(
  mode: ComposerMode,
  t: (key: string) => string,
): { title: string; body: string } {
  switch (mode) {
    case "whatsapp_window_closed":
      return {
        title: t("inbox.composer.windowClosedTitle"),
        body: t("inbox.composer.windowClosedBody"),
      };
    case "facebook_window_closed":
      return {
        title: t("inbox.composer.closed.messengerTitle"),
        body: t("inbox.composer.messengerWindowClosedBody"),
      };
    case "instagram_window_closed":
      return {
        title: t("inbox.composer.closed.instagramTitle"),
        body: t("inbox.composer.messengerWindowClosedBody"),
      };
    case "whatsapp_freeform":
    case "facebook_freeform":
    case "instagram_freeform":
    case "unsupported_channel":
      return {
        title: t("inbox.composer.outboundUnavailableTitle"),
        body: t("inbox.composer.outboundUnavailable"),
      };
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}
